import { TABLE_COST_LIMIT_MESSAGE } from './constants'
import TableProgressPanel from '../components/TableProgressPanel'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { Button } from '@/components/button/Button'
import { AnalysisActions } from './store/slice'
import TableProcessingStatus from '../components/TableProcessingStatus'
import { AlertErrorCircle } from '@/assets/icons'

export default function AnalysisProgress({
  tableId,
  label = 'Document analysis',
  canEdit,
  onProgress,
  onViewErrors,
  onViewPaused,
  pendingQuestions = 0,
  documentCount = 0,
  analysisQuestionCount,
}: {
  tableId: string
  label?: string
  canEdit: boolean
  onProgress: () => void
  onViewErrors?: () => void
  onViewPaused?: () => void
  pendingQuestions?: number
  documentCount?: number
  analysisQuestionCount: number
}) {
  const dispatch = useDispatch()
  const state = useSelector((root: RootState) => root.tableAnalysis)
  const run = state.tableId === tableId ? state.run : null
  const costLimitReached = run?.cost_limit_reached === true
  const completed = run?.counts.completed ?? 0
  const ready = run?.counts.ready ?? 0
  const nextAction = run?.next_action
  const actionLabel = nextAction === 'retry' ? 'Retry analysis' : nextAction === 'continue' ? 'Continue analysis' : 'Run analysis'
  const hasChanges = pendingQuestions > 0 || ready > 0
  const pending = run ? run.counts.pending + run.counts.running : 0
  useEffect(() => {
    dispatch(AnalysisActions.watch(tableId))
    return () => {
      dispatch(AnalysisActions.stop(tableId))
    }
  }, [dispatch, tableId])
  const failed = run?.counts.failed ?? 0
  const runId = run?.id
  useEffect(() => {
    // Mounting the progress watcher does not mean the table data changed.
    if (runId !== undefined) onProgress()
  }, [completed, ready, pending, failed, runId, onProgress])
  if (state.tableId !== tableId) return null
  if (!run && !state.error && !hasChanges) return null
  const cancelled = run?.counts.cancelled ?? 0
  const total = completed + ready + pending + failed + cancelled
  const showStatus = pending > 0 || ready > 0 || failed > 0 || cancelled > 0
  if (!costLimitReached && !showStatus && !state.error && !(canEdit && (hasChanges || nextAction))) return null
  const statusText = [
    ready > 0 ? `${ready} ${ready === 1 ? 'document' : 'documents'} ready` : null,
    canEdit && pendingQuestions > 0 ? `${pendingQuestions} ${pendingQuestions === 1 ? 'question' : 'questions'} to run` : null,
    !hasChanges && completed > 0 ? `${completed} ${completed === 1 ? 'document' : 'documents'} completed` : null,
    failed > 0 ? `${failed} failed` : null,
    cancelled > 0 ? `${cancelled} canceled` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  if (costLimitReached && pending === 0) {
    const pausedCount = ready + cancelled
    return (
      <section aria-label={label} className="w-full min-w-0 overflow-hidden rounded-md border border-brand-neutral-200 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 bg-yellow-50 py-3 pl-4 pr-2">
          <div role="alert" className="flex min-w-0 flex-1 items-start gap-2 text-xs font-medium">
            <AlertErrorCircle className="h-5 w-5 shrink-0 text-yellow-800" aria-hidden />
            <p>{pausedCount > 0 ? TABLE_COST_LIMIT_MESSAGE : 'This table has reached its processing limit and further analysis is paused. Contact support to increase the limit.'}</p>
          </div>
          <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-3">
            {pausedCount > 0 && onViewPaused && <Button size="xs" variant="plain" color="secondary" onClick={onViewPaused}>View paused files ({pausedCount})</Button>}
          </div>
        </div>
        {state.error && <p role="alert" className="px-4 pb-3">{state.error}</p>}
      </section>
    )
  }
  return (
    <TableProgressPanel label={label} onViewErrors={failed > 0 ? onViewErrors : undefined}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {state.error ? (
          <p role="alert">{state.error}</p>
        ) : (
          (showStatus || (canEdit && hasChanges)) && (
            <TableProcessingStatus active={pending > 0} completed={completed} total={total}>
              {statusText}
            </TableProcessingStatus>
          )
        )}
        {!costLimitReached && canEdit && hasChanges && pending > 0 && <p className="text-brand-neutral-600">Changes can run when the current analysis finishes.</p>}
        {canEdit && pendingQuestions > 0 && !documentCount && <p className="text-brand-neutral-600">Add documents to this table to run analysis.</p>}
        {canEdit && ready > 0 && !analysisQuestionCount && <p className="text-brand-neutral-600">Add a question to analyze these documents.</p>}
        {canEdit && (
          <div className="flex flex-auto flex-wrap items-center justify-end gap-2">
            {!costLimitReached && pending === 0 && nextAction && (
              <Button
                size="xs"
                variant="tonal"
                className="shrink-0 whitespace-nowrap disabled:!ring-0"
                disabled={state.saving || !documentCount || !analysisQuestionCount}
                onClick={() => dispatch(AnalysisActions.control({ tableId, action: 'run-updated' }))}
              >
                {actionLabel}
              </Button>
            )}
            {pending > 0 && (
              <Button
                size="xs"
                variant="plain"
                className="shrink-0 whitespace-nowrap"
                color="destructive"
                disabled={state.saving}
                onClick={() => dispatch(AnalysisActions.control({ tableId, action: 'cancel' }))}
              >
                Cancel analysis
              </Button>
            )}
          </div>
        )}
      </div>
    </TableProgressPanel>
  )
}
